import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Eye, Video, Loader2 } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import Sidebar from '../components/layout/Sidebar'
import { dashboardAPI } from '../services/api'
import { decodeHtmlEntities } from '../utils/displayText'

/**
 * Lists meetings the user can access only via org admin or team-browse visibility (not organiser / invitee).
 * Main dashboard and calendar stay “involvement-only”; this is the browse surface for oversight.
 */
export default function OrgOversightMeetingsPage() {
  const [meetings, setMeetings] = useState([])
  const [intro, setIntro] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await dashboardAPI.getOversightMeetings(150)
      const data = res?.data ?? res
      setMeetings(Array.isArray(data?.meetings) ? data.meetings : [])
      setIntro(typeof data?.intro === 'string' ? data.intro : '')
    } catch (e) {
      setError(e?.message || 'Failed to load')
      setMeetings([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />
      <main className="ml-0 lg:ml-[280px] min-h-screen px-4 pt-20 lg:pt-10 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-start gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
              <Eye className="w-5 h-5 text-teal-700" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin-visible meetings</h1>
              <p className="text-gray-600 mt-1 max-w-3xl">
                Meetings you can open because of org-wide or team admin visibility, not because you are the organiser or on
                the invite list. Your day-to-day meetings stay on the{' '}
                <Link to="/dashboard" className="text-teal-700 font-medium hover:underline">
                  dashboard
                </Link>{' '}
                and{' '}
                <Link to="/calendar" className="text-teal-700 font-medium hover:underline">
                  calendar
                </Link>
                .
              </p>
            </div>
          </div>

          {intro ? (
            <div className="mt-6 rounded-2xl border border-amber-100 bg-amber-50/80 px-4 py-3 text-sm text-amber-950 max-w-4xl">
              {intro}
            </div>
          ) : null}

          {error ? <p className="mt-8 text-sm text-red-600">{error}</p> : null}

          {loading ? (
            <div className="mt-10 flex items-center gap-2 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin" />
              Loading…
            </div>
          ) : (
            <ul className="mt-8 space-y-3 max-w-4xl">
              {meetings.length === 0 && !error ? (
                <li className="text-gray-600 text-sm py-6 border border-dashed border-gray-200 rounded-2xl px-4 text-center">
                  No admin-only visible meetings right now. If you are an org admin, org-wide meetings that you are not
                  invited to will show up here; everyone else typically sees an empty list.
                </li>
              ) : null}
              {meetings.map((m) => {
                const id = m.meetingId || m.id
                const title = decodeHtmlEntities(m.title || 'Untitled')
                const start = m.startTime || m.scheduledStartTime
                let dateLabel = '—'
                if (start) {
                  try {
                    dateLabel = format(parseISO(start), 'MMM d, yyyy')
                  } catch {
                    dateLabel = '—'
                  }
                }
                return (
                  <li key={id}>
                    <Link
                      to={`/meeting/${encodeURIComponent(id)}`}
                      className="flex items-center gap-4 p-4 rounded-2xl border border-gray-200 bg-white hover:border-teal-200 hover:shadow-sm transition"
                    >
                      <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center shrink-0">
                        <Video className="w-5 h-5 text-gray-500" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 truncate">{title}</p>
                        <p className="text-sm text-gray-500">{dateLabel}</p>
                      </div>
                      <span className="text-xs font-medium text-amber-800 bg-amber-50 border border-amber-100 px-2 py-1 rounded-lg shrink-0">
                        Not on invite list
                      </span>
                    </Link>
                  </li>
                )
              })}
            </ul>
          )}
        </motion.div>
      </main>
    </div>
  )
}
